import { wrapSelection, setParameter } from "../tools"
import { HtmlEditorCommander } from "aldoo-ra/Commander"

const { runCommand } = HtmlEditorCommander

const alignmentTypes = {
  left: "ae-align-left",
  center: "ae-align-center",
  right: "ae-align-right",
  justify: "ae-align-justify",
}

// Store the currently selected image globally
let currentSelectedImage = null

// Function to set the currently selected image (called from image click handler)
export const setCurrentSelectedImage = (image) => {
  currentSelectedImage = image
}

// Function to clear the selected image
export const clearCurrentSelectedImage = () => {
  currentSelectedImage = null
}

/**
 * Handles text alignment
 * @param {Object} params - Plugin parameters
 * @param {string} params.action - Alignment type (left|center|right|justify|remove|apply)
 * @param {Object} params.data - Additional data for apply action
 */
const Align = ({ action, data }) => {
  // Handle apply action from AlignmentPicker
  if (action === 'apply' && data && data.action) {
    return Align({ action: data.action })
  }
  // Handle remove action
  if (action === 'remove') {
    const selection = window.getSelection()
    if (selection.rangeCount === 0) return

    const range = selection.getRangeAt(0)
    let element = range.commonAncestorContainer

    // If it's a text node, get the parent element
    if (element.nodeType === Node.TEXT_NODE) {
      element = element.parentElement
    }

    // Walk up the DOM to find an element with alignment classes
    while (element && element !== document.body) {
      const hasAlignmentClass = Object.values(alignmentTypes).some(className => 
        element.classList?.contains(className)
      )
      
      if (hasAlignmentClass) {
        // Remove all alignment classes
        Object.values(alignmentTypes).forEach((className) => {
          element.classList.remove(className)
        })
        break
      }
      
      element = element.parentElement
    }
    return
  }

  // Check if we're dealing with an image first
  const selection = window.getSelection()
  let targetImage = null

  // First, check if we have a stored selected image
  if (currentSelectedImage) {
    targetImage = currentSelectedImage
  }

  // If no stored image, fall back to selection-based detection
  if (!targetImage && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0)
    let element = range.commonAncestorContainer

    // If it's a text node, get the parent element
    if (element.nodeType === Node.TEXT_NODE) {
      element = element.parentElement
    }

    // Check if we're selecting an image
    if (element.tagName === 'IMG') {
      targetImage = element
    } else {
      // Look for an image within the selection
      targetImage = element.querySelector('img')
      if (!targetImage && range.startContainer.nodeType === Node.ELEMENT_NODE) {
        const startElement = range.startContainer.childNodes[range.startOffset]
        if (startElement && startElement.tagName === 'IMG') {
          targetImage = startElement
        }
      }
    }
  }

    // If we found an image, use the same alignment approach as the image plugin
    if (targetImage) {
      // Use the exact same procedure as the image options menu
      runCommand("image.align", { selectedImage: targetImage, alignment: action })
      
      // Maintain the image selection by creating a new selection range on the image
      // This ensures the transform gizmo stays active
      setTimeout(() => {
        const newSelection = window.getSelection()
        newSelection.removeAllRanges()
        const newRange = document.createRange()
        newRange.selectNode(targetImage)
        newSelection.addRange(newRange)
        
        // Trigger a click event on the image to maintain the gizmo
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window
        })
        targetImage.dispatchEvent(clickEvent)
      }, 10)
      
      return
    }

  // Handle text alignment by wrapping in paragraph
  const node = wrapSelection("p")

  // If no node to align, exit
  if (!node) {
    return
  }

  // Remove any existing alignment classes
  Object.values(alignmentTypes).forEach((className) => {
    node.classList.remove(className)
  })

  // Apply new alignment if not left (default)
  const alignClass = alignmentTypes[action]
  if (alignClass && action !== 'left') {
    setParameter(node, alignClass)
  }
}

// Add static types for type checking and auto-completion
Align.types = alignmentTypes

export default Align
